import { ExternalLinkIcon, LinkIcon, PaperclipIcon } from 'lucide-react';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { getAttachmentDisplayLabel } from '../../lib/shortenAttachmentUrl';
import type { ConversationAttachmentResponse, PhraseAttachmentInPhraseResponse } from '../../model/api/types';
import { useAttachmentUrl } from '../../model/useAttachmentUrl';
import { AttachmentLink } from './AttachmentLink';
import { AttachmentScrollButton } from './AttachmentScrollButton';

/**
 * Пропсы компонента {@link AttachmentItem}.
 */
type Props = {
  /** Вложение из ответа GET /conversation/{id}.attachments или из фразы транскрипта */
  attachment: ConversationAttachmentResponse | PhraseAttachmentInPhraseResponse;
  /** ID фразы вложения; используется для прокрутки к фразе из таба «Вложения». */
  phraseId?: number | null;
  /** Готовая подпись таймстемпа фразы (формат `M:SS - M:SS`); если null — кнопка не показывается. */
  phraseTimeLabel?: string | null;
  /** Колбэк прокрутки транскрипта к фразе вложения. Если не передан — кнопка не показывается. */
  onScrollToPhrase?: (phraseId: number) => void;
  /** Время отправки сообщения вложения (формат `dd.MM.yyyy HH:mm`); если null — кнопка не показывается. */
  messageTimeLabel?: string | null;
  /** Колбэк прокрутки переписки к сообщению вложения. Если не передан — кнопка не показывается. */
  onScrollToMessage?: (messageId: number) => void;
  /** Компактный режим (используется в облаке транскрипта) — без бордера и фоновой плашки. */
  compact?: boolean;
  className?: string;
};

/**
 * Универсальное отображение вложения диалога.
 *
 * Используется в табе «Вложения» и под склеенным облаком фраз транскрипта.
 * Длинная ссылка выводится в сокращённом виде (без схемы) и обрезается с
 * многоточием по доступной ширине; полный URL — в title и href/обработчике.
 *
 * Файл открывается в новой вкладке из источника по общему правилу вложений
 * ({@link useAttachmentUrl}) — так же, как в ленте переписки: внешняя ссылка — напрямую,
 * файл хранилища — после получения его адреса. Недоступное вложение помечено и не открывается.
 */
export const AttachmentItem: FC<Props> = ({
  attachment,
  phraseId,
  phraseTimeLabel,
  onScrollToPhrase,
  messageTimeLabel,
  onScrollToMessage,
  compact = false,
  className,
}) => {
  const { t } = useTranslation('dialogs');
  const { isLoading, isUnavailable, openInNewTab } = useAttachmentUrl(attachment, false);
  const name = getAttachmentDisplayLabel(attachment) || t('textFeed.attachment');

  // phraseId либо берётся из вложения карточки диалога, либо передаётся пропом из транскрипта
  const effectivePhraseId = phraseId ?? ('phraseId' in attachment ? (attachment.phraseId ?? null) : null);
  const messageId = 'messageId' in attachment ? (attachment.messageId ?? null) : null;
  const showPhraseButton = onScrollToPhrase != null && effectivePhraseId != null && phraseTimeLabel != null;
  const showMessageButton = onScrollToMessage != null && messageId != null && messageTimeLabel != null;

  const handlePhraseScrollClick = useCallback(() => {
    if (effectivePhraseId != null) {
      onScrollToPhrase?.(effectivePhraseId);
    }
  }, [onScrollToPhrase, effectivePhraseId]);

  const handleMessageScrollClick = useCallback(() => {
    if (messageId != null) {
      onScrollToMessage?.(messageId);
    }
  }, [onScrollToMessage, messageId]);

  return (
    <div
      className={cn(
        'flex w-full min-w-0 items-center gap-3',
        !compact && 'rounded-md border border-border bg-card p-3',
        className,
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground',
          compact ? 'size-6' : 'size-8',
        )}
      >
        {attachment.isExternalUrl ? (
          <LinkIcon className={compact ? 'size-3' : 'size-4'} />
        ) : (
          <PaperclipIcon className={compact ? 'size-3' : 'size-4'} />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <AttachmentLink
          attachment={attachment}
          name={name}
          isUnavailable={isUnavailable}
          isLoading={isLoading}
          onOpen={openInNewTab}
          compact={compact}
        />
        {isUnavailable && <span className="text-xs text-muted-foreground">{t('attachmentUnavailable')}</span>}
      </div>
      {attachment.isExternalUrl && !compact && (
        <span className="hidden shrink-0 items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground sm:inline-flex">
          <ExternalLinkIcon className="size-3" />
          {t('attachmentExternal')}
        </span>
      )}
      {showPhraseButton && (
        <AttachmentScrollButton
          timeLabel={phraseTimeLabel}
          ariaLabel={t('attachmentScrollToPhrase')}
          compact={compact}
          onClick={handlePhraseScrollClick}
        />
      )}
      {showMessageButton && (
        <AttachmentScrollButton
          timeLabel={messageTimeLabel}
          ariaLabel={t('attachmentScrollToMessage')}
          compact={compact}
          onClick={handleMessageScrollClick}
        />
      )}
    </div>
  );
};
